import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, LogOut, Package, FolderOpen, Search, Edit2, Trash2, Plus, Save, X, ChevronLeft, ChevronRight, Upload, Image as ImageIcon, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin Login Component
function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      if (!response.ok) {
        throw new Error('Mot de passe incorrect');
      }
      
      const data = await response.json();
      localStorage.setItem('admin_token', data.token);
      onLogin(data.user);
      toast.success('Connexion réussie');
    } catch (error) {
      toast.error(error.message || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#DC2626] rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold">Backoffice Admin</h1>
          <p className="text-gray-500 mt-2">Connectez-vous pour accéder à l&apos;administration</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="password">Mot de passe administrateur</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Entrez le mot de passe"
              className="mt-2"
              required
              data-testid="admin-password-input"
            />
          </div>
          
          <Button 
            type="submit" 
            className="w-full bg-[#DC2626] hover:bg-[#B91C1C]"
            disabled={loading}
            data-testid="admin-login-btn"
          >
            {loading ? 'Connexion...' : 'Se connecter'}
          </Button>
        </form>
      </div>
    </div>
  );
}

// Image Dropzone Component
function ImageDropzone({ currentImage, onImageChange, label = "Image" }) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentImage || '');
  const [inputMode, setInputMode] = useState('url'); // 'url' or 'upload'
  const fileInputRef = useRef(null);

  useEffect(() => {
    setPreviewUrl(currentImage || '');
  }, [currentImage]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const uploadFile = async (file) => {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez PNG, JPG, SVG ou WebP');
      return null;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10MB)');
      return null;
    }

    setIsUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        body: uploadFormData
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'upload');
      }

      const data = await response.json();
      const imageUrl = `${API_URL}/api/upload/${data.file_id}`;
      return imageUrl;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload de l\'image');
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      const imageUrl = await uploadFile(file);
      if (imageUrl) {
        setPreviewUrl(imageUrl);
        onImageChange(imageUrl);
        toast.success('Image uploadée avec succès');
      }
    }
  }, [onImageChange]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const imageUrl = await uploadFile(file);
      if (imageUrl) {
        setPreviewUrl(imageUrl);
        onImageChange(imageUrl);
        toast.success('Image uploadée avec succès');
      }
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setPreviewUrl(url);
    onImageChange(url);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => setInputMode('upload')}
            className={`px-3 py-1 text-xs rounded-md transition-colors ${
              inputMode === 'upload' ? 'bg-white shadow-sm text-[#DC2626]' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Upload className="w-3 h-3 inline mr-1" />
            Upload
          </button>
          <button
            type="button"
            onClick={() => setInputMode('url')}
            className={`px-3 py-1 text-xs rounded-md transition-colors ${
              inputMode === 'url' ? 'bg-white shadow-sm text-[#DC2626]' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <ImageIcon className="w-3 h-3 inline mr-1" />
            URL
          </button>
        </div>
      </div>

      <div className="flex gap-4">
        {/* Preview */}
        <div className="w-32 h-32 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 border-2 border-gray-200">
          {previewUrl ? (
            <img 
              src={previewUrl} 
              alt="Preview"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
                e.target.parentElement.innerHTML = '<span class="text-xs text-gray-400">Image invalide</span>';
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-8 h-8 text-gray-300" />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="flex-1">
          {inputMode === 'upload' ? (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`
                relative border-2 border-dashed rounded-lg p-4 h-32 
                flex flex-col items-center justify-center cursor-pointer
                transition-all duration-200
                ${isDragging 
                  ? 'border-[#DC2626] bg-red-50' 
                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                }
                ${isUploading ? 'pointer-events-none opacity-70' : ''}
              `}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {isUploading ? (
                <>
                  <Loader2 className="w-8 h-8 text-[#DC2626] animate-spin mb-2" />
                  <p className="text-sm text-gray-600">Upload en cours...</p>
                </>
              ) : isDragging ? (
                <>
                  <Upload className="w-8 h-8 text-[#DC2626] mb-2" />
                  <p className="text-sm text-[#DC2626] font-medium">Déposez l&apos;image ici</p>
                </>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600 text-center">
                    <span className="font-medium text-[#DC2626]">Cliquez</span> ou glissez-déposez
                  </p>
                  <p className="text-xs text-gray-400 mt-1">PNG, JPG, SVG, WebP (max 10MB)</p>
                </>
              )}
            </div>
          ) : (
            <div className="h-32 flex flex-col justify-center">
              <Input
                value={previewUrl}
                onChange={handleUrlChange}
                placeholder="https://example.com/image.jpg"
                className="mb-2"
                data-testid="image-url-input"
              />
              <p className="text-xs text-gray-500">Collez l&apos;URL d&apos;une image externe</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Product Edit Modal
function ProductEditModal({ product, categories, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name_fr: product?.name_fr || '',
    name_en: product?.name_en || '',
    description_fr: product?.description_fr || '',
    description_en: product?.description_en || '',
    images: product?.images || [''],
    category_id: product?.category_id || '',
    slug: product?.slug || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formData);
      toast.success('Produit mis à jour');
      onClose();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">Modifier le produit</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Image with Drag & Drop */}
          <ImageDropzone
            currentImage={formData.images[0]}
            onImageChange={(url) => setFormData({...formData, images: [url]})}
            label="Image du produit"
          />
          
          {/* Names */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nom (FR)</Label>
              <Input
                value={formData.name_fr}
                onChange={(e) => setFormData({...formData, name_fr: e.target.value})}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Nom (EN)</Label>
              <Input
                value={formData.name_en}
                onChange={(e) => setFormData({...formData, name_en: e.target.value})}
                className="mt-2"
              />
            </div>
          </div>
          
          {/* Descriptions */}
          <div>
            <Label>Description (FR)</Label>
            <textarea
              value={formData.description_fr}
              onChange={(e) => setFormData({...formData, description_fr: e.target.value})}
              className="mt-2 w-full border rounded-lg p-3 min-h-[100px] text-sm"
              placeholder="Description en français..."
            />
          </div>
          
          <div>
            <Label>Description (EN)</Label>
            <textarea
              value={formData.description_en}
              onChange={(e) => setFormData({...formData, description_en: e.target.value})}
              className="mt-2 w-full border rounded-lg p-3 min-h-[100px] text-sm"
              placeholder="Description in English..."
            />
          </div>
          
          {/* Category */}
          <div>
            <Label>Catégorie</Label>
            <select
              value={formData.category_id}
              onChange={(e) => setFormData({...formData, category_id: e.target.value})}
              className="mt-2 w-full border rounded-lg p-3"
            >
              <option value="">-- Sélectionner --</option>
              {categories.map(cat => (
                <option key={cat.category_id} value={cat.category_id}>
                  {cat.name_fr}
                </option>
              ))}
            </select>
          </div>
          
          {/* Slug */}
          <div>
            <Label>Slug (URL)</Label>
            <Input
              value={formData.slug}
              onChange={(e) => setFormData({...formData, slug: e.target.value})}
              className="mt-2"
              placeholder="mon-produit-nfc"
            />
          </div>
        </div>
        
        <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end gap-4">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-[#DC2626] hover:bg-[#B91C1C]"
            data-testid="product-save-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Category Edit Modal
function CategoryEditModal({ category, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name_fr: category?.name_fr || '',
    name_en: category?.name_en || '',
    image: category?.image || '',
    slug: category?.slug || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formData);
      toast.success('Catégorie mise à jour');
      onClose();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-lg">
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">Modifier la catégorie</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Image with Drag & Drop */}
          <ImageDropzone
            currentImage={formData.image}
            onImageChange={(url) => setFormData({...formData, image: url})}
            label="Image de la catégorie"
          />
          
          {/* Names */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nom (FR)</Label>
              <Input
                value={formData.name_fr}
                onChange={(e) => setFormData({...formData, name_fr: e.target.value})}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Nom (EN)</Label>
              <Input
                value={formData.name_en}
                onChange={(e) => setFormData({...formData, name_en: e.target.value})}
                className="mt-2"
              />
            </div>
          </div>
          
          {/* Slug */}
          <div>
            <Label>Slug (URL)</Label>
            <Input
              value={formData.slug}
              onChange={(e) => setFormData({...formData, slug: e.target.value})}
              className="mt-2"
            />
          </div>
        </div>
        
        <div className="bg-gray-50 border-t px-6 py-4 flex justify-end gap-4">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-[#DC2626] hover:bg-[#B91C1C]"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Main Admin Dashboard
function AdminDashboard({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const token = localStorage.getItem('admin_token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  // Load data
  useEffect(() => {
    loadData();
  }, [activeTab, page, search, selectedCategory]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'products') {
        const params = new URLSearchParams({
          limit: limit.toString(),
          skip: (page * limit).toString()
        });
        if (search) params.append('search', search);
        if (selectedCategory) params.append('category', selectedCategory);
        
        const response = await fetch(`${API_URL}/api/admin/products?${params}`, { headers });
        const data = await response.json();
        setProducts(data.products);
        setTotal(data.total);
      }
      
      // Always load categories for the filter
      const catResponse = await fetch(`${API_URL}/api/admin/categories`, { headers });
      const catData = await catResponse.json();
      setCategories(catData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProduct = async (productId, data) => {
    const response = await fetch(`${API_URL}/api/admin/products/${productId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data)
    });
    
    if (!response.ok) throw new Error('Failed to save');
    loadData();
  };

  const handleSaveCategory = async (categoryId, data) => {
    const response = await fetch(`${API_URL}/api/admin/categories/${categoryId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data)
    });
    
    if (!response.ok) throw new Error('Failed to save');
    loadData();
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/products/${productId}`, {
        method: 'DELETE',
        headers
      });
      
      if (!response.ok) throw new Error('Failed to delete');
      toast.success('Produit supprimé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.category_id === categoryId);
    return cat?.name_fr || 'Non classé';
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold text-[#DC2626]">LogoTag<sup className="text-[8px]">®</sup> Admin</h1>
            <span className="text-sm text-gray-500">Connecté en tant qu&apos;administrateur</span>
          </div>
          <Button variant="outline" onClick={onLogout} className="text-gray-600">
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8">
          <Button
            variant={activeTab === 'products' ? 'default' : 'outline'}
            onClick={() => { setActiveTab('products'); setPage(0); }}
            className={activeTab === 'products' ? 'bg-[#DC2626]' : ''}
          >
            <Package className="w-4 h-4 mr-2" />
            Produits ({total})
          </Button>
          <Button
            variant={activeTab === 'categories' ? 'default' : 'outline'}
            onClick={() => setActiveTab('categories')}
            className={activeTab === 'categories' ? 'bg-[#DC2626]' : ''}
          >
            <FolderOpen className="w-4 h-4 mr-2" />
            Catégories ({categories.length})
          </Button>
        </div>

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {/* Search & Filter */}
            <div className="p-4 border-b flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  placeholder="Rechercher un produit..."
                  className="pl-10"
                  data-testid="admin-search-input"
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => { setSelectedCategory(e.target.value); setPage(0); }}
                className="border rounded-lg px-4 py-2 min-w-[200px]"
              >
                <option value="">Toutes les catégories</option>
                {categories.map(cat => (
                  <option key={cat.category_id} value={cat.category_id}>
                    {cat.name_fr}
                  </option>
                ))}
              </select>
            </div>

            {/* Products List */}
            {loading ? (
              <div className="p-8 text-center text-gray-500">Chargement...</div>
            ) : (
              <div className="divide-y">
                {products.map(product => (
                  <div 
                    key={product.product_id} 
                    className="p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
                    data-testid={`admin-product-${product.slug}`}
                  >
                    {/* Image */}
                    <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      {product.images?.[0] && (
                        <img 
                          src={product.images[0]} 
                          alt={product.name_fr}
                          className="w-full h-full object-cover"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate">{product.name_fr}</h3>
                      <p className="text-sm text-gray-500 truncate">{product.slug}</p>
                      <span className="inline-block mt-1 px-2 py-0.5 bg-gray-100 text-xs rounded">
                        {getCategoryName(product.category_id)}
                      </span>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingProduct(product)}
                        data-testid={`edit-product-${product.slug}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteProduct(product.product_id)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-4 border-t flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  Page {page + 1} sur {totalPages} ({total} produits)
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="divide-y">
              {categories.map(category => (
                <div 
                  key={category.category_id} 
                  className="p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
                >
                  {/* Image */}
                  <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    {category.image && (
                      <img 
                        src={category.image} 
                        alt={category.name_fr}
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium">{category.name_fr}</h3>
                    <p className="text-sm text-gray-500">{category.slug}</p>
                  </div>
                  
                  {/* Actions */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingCategory(category)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {editingProduct && (
        <ProductEditModal
          product={editingProduct}
          categories={categories}
          onSave={(data) => handleSaveProduct(editingProduct.product_id, data)}
          onClose={() => setEditingProduct(null)}
        />
      )}
      
      {editingCategory && (
        <CategoryEditModal
          category={editingCategory}
          onSave={(data) => handleSaveCategory(editingCategory.category_id, data)}
          onClose={() => setEditingCategory(null)}
        />
      )}
    </div>
  );
}

// Main Component
export function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      setChecking(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else {
        localStorage.removeItem('admin_token');
      }
    } catch (error) {
      localStorage.removeItem('admin_token');
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setUser(null);
    toast.success('Déconnecté');
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }

  if (!user) {
    return <AdminLogin onLogin={setUser} />;
  }

  return <AdminDashboard user={user} onLogout={handleLogout} />;
}

export default AdminPage;
